/**
 * Delivery send-log data — shared types, event/source metadata, and sort
 * helpers for Messaging → Delivery.
 *
 * Data source: the LIVE notification-history ops (listMessageHistory /
 * getMessageHistoryDetail) via message-history-api.ts. The fictional
 * DELIVERY_ROWS/_DGEN_* seed was DELETED 2026-07-18 per the Voyage docket
 * §2.1(b) — no baked send-log content remains in this module.
 */
import { MSG_CHANNEL } from "./messaging-shared";
import { t as i18nT } from "../../lib/i18n";

export interface DelivEvent {
  type: string; at: string; meta?: string;
  /** Token names that rendered empty on THIS attempt (on the `send` event).
   *  Omitted when none blanked (CC 2026-09-03). */
  missingTokens?: string[];
}
export interface DeliveryRow {
  id: string; channel: string;
  sender: { name: string; from: string };
  recipient: { name: string; to: string };
  source: { kind: string; label: string };
  subject?: string; text?: string; html?: string;
  sent: string; events: DelivEvent[];
  /* Notification-history provenance (listMessageHistory / getMessageHistoryDetail). */
  displayId?: string; status?: string; provider?: string; credentialSource?: string;
  replyTo?: string; bodyUnavailable?: boolean;
  /** Where the shown content came from (CC 2026-09-03 §2, live on the wire):
   *  `dispatch` = the bytes the customer received, stored at hand-off;
   *  `template` = a send made before rendered content was stored, so the
   *  TEMPLATE is shown and its tokens are unresolved BY DESIGN. */
  contentSource?: "dispatch" | "template";
  /** The provider's own id for this send — evidence for a support ticket. */
  providerMessageId?: string;
  /** When a queued row is planned to go out. A queued send has no `sent`. */
  plannedAt?: string;
  /** Row-level union of tokens that rendered empty across the dispatch's
   *  attempts. On a `dispatch` row it means a customer received a message with
   *  a hole in it; on a `template` row it carries no information (unresolved
   *  tokens are the point) and is ignored. */
  hasMissingTokens?: boolean;
}

/* Tone is the dot colour, and the dot has one job: did this send succeed or
 * fail (CD 2026-09-04). So anything that left the building successfully is
 * `success` — `send`/`accepted`/`sent` were `neutral`, which drew a grey dot
 * next to "Sent" and read as "nothing happened yet". Neutral now means exactly
 * that: not yet an outcome (`queued`) or no send attempted (`cancelled`,
 * `unsubscribed`). `success` on `sent` states provider acceptance, not inbox
 * delivery — `delivered` is the stronger claim and keeps its own entry. */
export const DELIV_EVENTS: Record<string, { label: string; labelKey: string; tone: string; icon: string; blurb: string }> = {
  send: { labelKey: "messaging.deliveryData.sent", get label() { return i18nT("messaging.deliveryData.sent"); }, tone: "success", icon: "send", blurb: "Accepted by the sending gateway." },
  delivered: { labelKey: "messaging.deliveryData.delivered", get label() { return i18nT("messaging.deliveryData.delivered"); }, tone: "success", icon: "check", blurb: "Confirmed delivered to the recipient's inbox / handset." },
  opened: { labelKey: "messaging.deliveryData.opened", get label() { return i18nT("messaging.deliveryData.opened"); }, tone: "info", icon: "eye", blurb: "Recipient opened the message." },
  clicked: { labelKey: "messaging.deliveryData.linkClicked", get label() { return i18nT("messaging.deliveryData.linkClicked"); }, tone: "primary", icon: "link", blurb: "Recipient clicked a link in the message." },
  soft_bounce: { labelKey: "messaging.deliveryData.softBounce", get label() { return i18nT("messaging.deliveryData.softBounce"); }, tone: "warn", icon: "warn", blurb: "Temporary failure — mailbox full or server busy. Will retry." },
  hard_bounce: { labelKey: "messaging.deliveryData.hardBounce", get label() { return i18nT("messaging.deliveryData.hardBounce"); }, tone: "danger", icon: "warn", blurb: "Permanent failure — address invalid or rejected. Suppressed." },
  // Notification-history lattice (listMessageHistory / getMessageHistoryDetail).
  queued: { labelKey: "messaging.deliveryData.queued", get label() { return i18nT("messaging.deliveryData.queued"); }, tone: "neutral", icon: "send", blurb: "Queued for sending — no provider events yet." },
  accepted: { labelKey: "messaging.deliveryData.accepted", get label() { return i18nT("messaging.deliveryData.accepted"); }, tone: "success", icon: "send", blurb: "Accepted by the sending provider." },
  deferred: { labelKey: "messaging.deliveryData.deferred", get label() { return i18nT("messaging.deliveryData.deferred"); }, tone: "warn", icon: "warn", blurb: "Temporary failure — the provider will retry." },
  bounced: { labelKey: "messaging.deliveryData.bounced", get label() { return i18nT("messaging.deliveryData.bounced"); }, tone: "danger", icon: "warn", blurb: "Permanent delivery failure — the address was rejected." },
  complained: { labelKey: "messaging.deliveryData.complaint", get label() { return i18nT("messaging.deliveryData.complaint"); }, tone: "danger", icon: "warn", blurb: "Recipient marked the message as spam." },
  dropped: { labelKey: "messaging.deliveryData.dropped", get label() { return i18nT("messaging.deliveryData.dropped"); }, tone: "danger", icon: "warn", blurb: "Dropped by the provider before delivery." },
  unsubscribed: { labelKey: "messaging.deliveryData.unsubscribed", get label() { return i18nT("messaging.deliveryData.unsubscribed"); }, tone: "neutral", icon: "x", blurb: "Recipient unsubscribed from this stream." },
  failed: { labelKey: "common.status.failed", get label() { return i18nT("common.status.failed"); }, tone: "danger", icon: "warn", blurb: "The send failed." },
  suppressed: { labelKey: "messaging.deliveryData.suppressed", get label() { return i18nT("messaging.deliveryData.suppressed"); }, tone: "warn", icon: "x", blurb: "Not sent — the recipient is on the suppression list." },
  /* Row STATUSES that no event type spells (CC 2026-09-03 §2). The badge reads
     `status`, and `sent`/`cancelled` had no entry here — `cancelled` fell back
     to the `send` entry and rendered as "Sent", which is the exact failure the
     status-not-event-type ruling exists to stop. */
  sent: { labelKey: "messaging.deliveryData.sent", get label() { return i18nT("messaging.deliveryData.sent"); }, tone: "success", icon: "send", blurb: "Handed to the sending provider." },
  cancelled: { labelKey: "messaging.deliveryData.cancelled", get label() { return i18nT("messaging.deliveryData.cancelled", "Cancelled"); }, tone: "neutral", icon: "x", blurb: "Cancelled before it was sent." },
};

export const DELIV_SOURCES: Record<string, { icon: string; color: string }> = {
  flow: { icon: "send", color: "#5142C7" },
  campaign: { icon: "chart", color: "#2D60EB" },
  ticketgroup: { icon: "ticket", color: "#916D0F" },
  onsite: { icon: "qr", color: "#027D47" },
  manual: { icon: "user", color: "#697180" },
  trigger: { icon: "refresh", color: "#0E7490" },
};

export const SOURCE_FILTER_LABELS: Record<string, string> = {
  flow: "Flows", campaign: "Campaigns", ticketgroup: "Ticket-tier flows", onsite: "On-site", manual: "Manual sends", trigger: "Triggers",
};

const _DMON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function delivTimeVal(s: string): number {
  const m = /(\d{1,2})\s+([A-Za-z]{3}),?\s+(\d{1,2}):(\d{2}):(\d{2})/.exec(s || "");
  if (!m) return 0;
  return ((_DMON.indexOf(m[2]) * 31 + +m[1]) * 24 + +m[3]) * 3600 + +m[4] * 60 + +m[5];
}

export const lastEvent = (d: DeliveryRow): DelivEvent => d.events[d.events.length - 1];

/** The row's STATUS — what the badge must read (CC 2026-09-03 §2).
 *  A scheduled reminder carries a history entry of type `send` ("Queued for
 *  delivery") while its status is `queued`, so the latest EVENT TYPE says
 *  "Sent" about a message nobody has sent. The status is the authority; the
 *  event type only survives as a fallback for rows that carry no status. */
export const delivStatus = (d: DeliveryRow): string => d.status || lastEvent(d).type;

/** Tokens that blanked, only where that MEANS something: a `dispatch` row.
 *  Empty on `template` rows by design. */
export const missingTokensOf = (d: DeliveryRow): string[] =>
  d.contentSource === "template" ? [] : Array.from(new Set(d.events.flatMap((e) => e.missingTokens ?? [])));
/** Whether to mark the row at all — the row-level flag is the list's only
 *  signal (the names arrive with the detail fetch). */
export const hasMissingTokens = (d: DeliveryRow): boolean =>
  d.contentSource !== "template" && (!!d.hasMissingTokens || missingTokensOf(d).length > 0);

/** The statuses a row can hold — the badge and the status filter's vocabulary.
 *  Event types (opened / clicked / …) are timeline entries, not row states. */
export const DELIV_ROW_STATUSES = ["queued", "sent", "delivered", "bounced", "failed", "suppressed", "cancelled"];

export function sortDeliveries(arr: DeliveryRow[], sort: { key: string; dir: "asc" | "desc" }): DeliveryRow[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const val = (d: DeliveryRow): string | number => {
    switch (sort.key) {
      case "recipient": return d.recipient.name.toLowerCase();
      case "channel": return MSG_CHANNEL(d.channel).name.toLowerCase();
      case "message": return (d.subject || d.text || "").toLowerCase();
      case "sender": return d.sender.name.toLowerCase();
      case "source": return d.source.label.toLowerCase();
      case "latest": default: return delivTimeVal(lastEvent(d).at);
    }
  };
  return arr.slice().sort((a, b) => {
    const va = val(a), vb = val(b);
    if (typeof va === "number") return (va - (vb as number)) * dir;
    return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
  });
}
